import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { Modal } from "bootstrap"
import { acquireLoadingOverlay, settleNavigation } from "../lib/slow_visit_overlay"

// GOAT Store order-modal adapter (#13900) — the client half of
// output/ui-work-queue/store-recovery-20260923/MODAL_ADAPTER_CONTRACT.md v2.
//
// Mounted once per page on #store-order-modal-host (layouts/goatleads/
// _store_order_modal_host). Any same-origin `<a data-store-order-modal href=…>`
// anywhere on the page is intercepted: the href is fetched with `store_modal=1`
// appended, the server answers with ONE canonical gl_modal fragment, and that
// element is inserted here, shown, and removed again on close. The href stays
// the deep-link fallback (no JS, modifier/middle click, cross-origin, any
// failure), so the menu never loses a working path.
//
// Lifecycle guarantees (all covered in the Jest specs):
//   * one fetch per click while a load is in flight (no double open); an open
//     dialog is a separate state — a store link inside it tears the current dialog down
//     and loads the next, keeping the original external trigger for focus return;
//   * a modal shown INSIDE the dialog (the photo slot's crop modal) is settled and
//     disposed together with the dialog on every teardown, touching only the
//     backdrop that modal added — never another modal's on the page;
//   * every load is abortable and generation-stamped — close, reload, a Turbo
//     render or disconnect invalidates it, so a late response never reopens a
//     closed dialog; the shared GOAT overlay is released on every terminal path;
//   * the response is validated before it touches the DOM: exactly one root
//     `.modal`, no nested `.modal`, no `<script>` — <template> content included
//     (#14273 item 3); otherwise → href fallback;
//   * a redirect (sign-in, denial toast) is never inserted and never visited by
//     its target — the browser re-issues the PLAIN href as a navigation so the
//     server's own redirect, with its flash, runs there (see navigateAfterRedirect);
//   * a close asked for while the dialog is still fading IN (the X, Escape, a
//     backdrop click, the close event) is parked until `shown.bs.modal` and
//     then run — Bootstrap's hide() is a silent no-op during its own show
//     transition (#14273 item 1; the same parking lib/gl_modal_shell does);
//   * on `hidden.bs.modal` the Bootstrap instance is disposed (hide() has
//     already released the body lock), the element is removed and focus returns
//     to the trigger. A teardown that cannot wait for hide() — reload,
//     turbo:before-cache, disconnect — settles the modal first (settleModal:
//     Bootstrap's own hide run to its end synchronously), because
//     Modal.dispose() never releases the body lock.
//     Tab containment is the shared gl-modal-focus-trap that
//     ModalHelper#gl_modal attaches to every dialog (modal_helper.rb:58,64-72).
export const STORE_MODAL_PARAM = "store_modal"
export const TRIGGER_SELECTOR = "a[data-store-order-modal]"
export const CLOSE_EVENT = "store-order-modal:close"
export const RELOAD_EVENT = "store-order-modal:reload"
export const SHOWN_EVENT = "store-order-modal:shown"
export const HIDDEN_EVENT = "store-order-modal:hidden"

// The one place this adapter leaves the page. A seam (not a monkey-patch) so the
// Jest spec can observe navigation — jsdom's window.location is read-only.
export const browserNavigation = {
  assign(href) {
    window.location.assign(href)
  },
}

// The element that should receive focus when the dialog closes: the trigger
// itself while it is still rendered, otherwise the trigger of the shared menu
// (gl_more_menu, the GOAT Store menu) it lives in. `checkVisibility` is missing in older engines and in jsdom; treat
// that as visible so the plain-trigger path is unchanged there.
export function focusReturnTarget(trigger) {
  if (!trigger || !document.contains(trigger) || typeof trigger.focus !== "function") return null
  const visible = typeof trigger.checkVisibility === "function" ? trigger.checkVisibility() : true
  if (visible) return trigger
  return trigger.closest('[data-controller~="more-menu"]')?.querySelector('[data-more-menu-target="trigger"]') || trigger
}

export function sameOriginUrl(href) {
  if (!href) return null
  let url
  try {
    url = new URL(href, window.location.href)
  } catch {
    return null
  }
  if (url.origin !== window.location.origin) return null
  if (url.protocol !== "http:" && url.protocol !== "https:") return null
  return url
}

export function modalRequestUrl(url) {
  const request = new URL(url.href)
  request.searchParams.set(STORE_MODAL_PARAM, "1")
  return request
}

// Exactly one root `.modal`, no nested `.modal`, no `<script>` — anything else
// is not an order modal and must not be injected.
//
// `<template>` content is its own DocumentFragment: querySelectorAll on the
// parent never sees inside it (#14273 item 3). A `.modal` INSIDE a template is
// how the photo slot ships its crop dialog (stamped into the dialog at
// runtime), so template-hosted modals are admitted and counted apart from the
// root — but a `<script>` is refused wherever it sits, templates and nested
// templates included: it is only inert until the template is stamped.
export function extractModal(html) {
  const template = document.createElement("template")
  template.innerHTML = html
  const roots = Array.from(template.content.children)
  if (roots.length !== 1 || !roots[0].classList.contains("modal")) return null
  if (template.content.querySelectorAll(".modal").length !== 1) return null
  if (containsScript(template.content)) return null
  return roots[0]
}

function containsScript(fragment) {
  if (fragment.querySelector("script")) return true
  return Array.from(fragment.querySelectorAll("template")).some((nested) => containsScript(nested.content))
}

// Bootstrap 5.3's Modal.dispose() on a SHOWN (or transitioning) modal removes
// the backdrop, the focus trap and the listeners — but the body lock its show()
// wrote (body.modal-open, `overflow: hidden`, the scrollbar-width padding
// ScrollBarHelper puts on <body> and fixed/sticky content) is released only at
// the end of hide(): _hideModal → the backdrop's hide callback. And every step
// of show()/hide() is queued behind `transitionend` on the dialog or the
// backdrop (with Bootstrap's own fallback timer), so callbacks queued before a
// dispose() fire afterwards against the nulled instance: transitionComplete
// reads `_config.focus` (TypeError), _showElement re-appends the element to
// whatever <body> is current, and a hide's backdrop callback strips
// body.modal-open from under a NEWER dialog. A teardown the adapter cannot
// animate (reload, turbo:before-cache, disconnect) therefore SETTLES the modal:
// it fires the `transitionend` Bootstrap is waiting for — exactly what the
// fallback timer would do — so a show in flight completes, then runs
// Bootstrap's own hide() to its end the same way. Bootstrap releases the lock
// with its own code, nothing stays queued, and nothing private is read or
// replaced. The backdrop is the element captured at show() (see #backdrop):
// after a Turbo body swap it is detached, so a document query would miss it.
// Pinned with real Bootstrap in store_order_modal_body_lock.test.js.
export function settleModal(instance, dialog, backdrop) {
  const complete = (element) => element?.dispatchEvent(new Event("transitionend"))
  const completeBackdrop = () => complete(backdrop)
  const completeDialog = () => {
    complete(dialog) // _hideModal is queued on the root …
    complete(dialog.querySelector(".modal-dialog")) // … transitionComplete on .modal-dialog
  }
  completeBackdrop()
  completeDialog() // a show in flight is now shown; a hide in flight has run _hideModal
  instance.hide() // a no-op when already hidden or hiding
  completeDialog()
  completeBackdrop() // _hideModal → backdrop hide → lock released → hidden.bs.modal
}

// Bootstrap appends the dialog's backdrop to <body> synchronously inside
// show(); the element then stays the instance's for life (dispose() removes,
// never replaces it). The one backdrop that show() added is ours.
function backdropAddedBy(show) {
  const before = new Set(document.querySelectorAll(".modal-backdrop"))
  show()
  return [...document.querySelectorAll(".modal-backdrop")].find((element) => !before.has(element)) ?? null
}

export default class extends Controller {
  // A fetch is in flight (double-click protection). Independent of #dialog: a
  // dialog can be OPEN with nothing pending, and an in-dialog store link
  // must then be able to switch dialogs (#14097 round 2). ("See usage costs"
  // is not one since 2026-10-08: it opens a template-popup OVER the dialog.)
  #pending = false
  #generation = 0
  #abort = null
  #release = null
  #trigger = null
  #dialog = null
  #instance = null
  #backdrop = null // the Bootstrap backdrop element show() created for #dialog
  // True between the dialog's own `show.bs.modal` and `shown.bs.modal`:
  // Bootstrap's hide() ignores callers in that window (_isTransitioning), so a
  // close requested then is parked and replayed the moment the show ends.
  #opening = false
  #hideWhenShown = false
  // Descendant Bootstrap modals shown INSIDE the dialog (the photo slot's crop
  // modal) → the backdrop each one owns, learned from its bubbling show event.
  // removeDialog() settles and disposes them before the subtree goes:
  // photo-crop's disconnect destroys its cropper but never disposes its Modal,
  // and an instance left behind keeps its backdrop, listeners and the body lock.
  #nested = new Map()

  connect() {
    this.onClick = this.onClick.bind(this)
    this.onClose = this.onClose.bind(this)
    this.onReload = this.onReload.bind(this)
    this.onBeforeCache = this.dispose.bind(this)
    document.addEventListener("click", this.onClick)
    document.addEventListener(CLOSE_EVENT, this.onClose)
    document.addEventListener(RELOAD_EVENT, this.onReload)
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
  }

  disconnect() {
    document.removeEventListener("click", this.onClick)
    document.removeEventListener(CLOSE_EVENT, this.onClose)
    document.removeEventListener(RELOAD_EVENT, this.onReload)
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    this.dispose()
  }

  onClick(event) {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    const trigger = event.target instanceof Element ? event.target.closest(TRIGGER_SELECTOR) : null
    if (!trigger) return
    if (trigger.target && trigger.target !== "_self") return
    if (trigger.hasAttribute("download")) return
    const url = sameOriginUrl(trigger.getAttribute("href"))
    if (!url) return // not ours: the browser navigates the plain link
    // Already on that page (e.g. the Aged Lead Store's own menu entry): a dialog
    // would duplicate the page's ids and controllers — let the link reload it.
    if (url.pathname === window.location.pathname) return

    event.preventDefault()
    // lib/slow_visit_overlay's own click listener ran BEFORE this one and, for
    // a trigger under a data-turbo="false" ancestor, began a native
    // navigation; a holder's release never hides the overlay while a screen
    // load is pending, so the goat would stay up over the opened dialog. No
    // navigation follows this click — say so before borrowing the overlay.
    settleNavigation()
    if (this.#pending) return // a fetch is in flight: two rapid clicks = one fetch
    // A product link INSIDE the open dialog switches products: the current
    // dialog is torn down cleanly (descendant modals included) and the next one
    // loads. The link goes with the dialog, so focus keeps returning to the
    // ORIGINAL external trigger (the menu entry) when the switched dialog closes.
    // A link outside the dialog is the new trigger, as before.
    const inside = this.#dialog?.contains(trigger) ?? false
    if (this.#dialog) this.dispose({ keepTrigger: inside })
    if (!inside) this.#trigger = trigger
    this.load(url)
  }

  onClose() {
    this.hide()
  }

  onReload(event) {
    const url = sameOriginUrl(event?.detail?.url)
    if (!url) {
      console.warn("[store-order-modal] ignored reload to an invalid or cross-origin URL", event?.detail?.url)
      return
    }
    this.dispose({ keepTrigger: true })
    this.load(url)
  }

  // Public for hosts that already validated a URL (a reload event, a test).
  async load(url) {
    this.#pending = true
    const generation = ++this.#generation
    const abort = (this.#abort = new AbortController())
    const release = (this.#release = acquireLoadingOverlay())
    let response
    try {
      response = await get(modalRequestUrl(url), {
        headers: { Accept: "text/html", "X-Store-Order-Modal": "1" },
        responseKind: "html",
        signal: abort.signal,
      })
      if (generation !== this.#generation) return
      if (response.redirected) return this.navigateAfterRedirect(response.response.url, url)
      if (!response.ok) return this.fallback(url, `HTTP ${response.statusCode}`)
      const html = await response.text
      if (generation !== this.#generation) return
      const dialog = extractModal(html)
      if (!dialog) return this.fallback(url, "response is not a single order modal")
      this.show(dialog, url)
    } catch (error) {
      if (error?.name === "AbortError") return // ours: close/reload/navigation cancelled it
      if (generation !== this.#generation) return
      this.fallback(url, error?.message || String(error))
    } finally {
      release()
      if (this.#release === release) this.#release = null
      if (this.#abort === abort) this.#abort = null
      if (generation === this.#generation) this.#pending = false
    }
  }

  show(dialog, url) {
    this.removeDialog() // a previous product, if any — never two dialogs
    this.#dialog = dialog
    this.#opening = false
    this.#hideWhenShown = false
    // The dialog is appended to <body>, NOT to the host: the host lives inside the
    // fixed navbar (.gl-navbar, z-index 999), whose stacking context would trap
    // the 1055 dialog beneath Bootstrap's body-level 1050 backdrop — every real
    // click inside the dialog then lands on the backdrop (chromedriver: "Other
    // element would receive the click: <div class=\"modal-backdrop fade show\">"),
    // which closes it. Bootstrap's own guidance is a top-level modal element;
    // Stimulus still connects the content's controllers wherever it is inserted,
    // and removeDialog() removes it by reference.
    document.body.appendChild(dialog)
    // Bootstrap dispatches every `*.bs.modal` event with bubbles: true, and a
    // modal can be SHOWN INSIDE the dialog at runtime — the croppable photo slot
    // mounts its crop modal from a <template> next to its input, i.e. as a
    // descendant of this element (#14097 B1). So the dialog's hidden handler
    // answers only its OWN hidden event, never a nested modal's, and it is not
    // `once`: a nested event must not consume it. removeDialog() detaches it.
    this.onHidden = (event) => {
      if (event.target === dialog) return this.afterHidden(url)
      // A nested modal's hide ran Bootstrap's _hideModal for THAT modal, which
      // strips body.modal-open although this dialog is still shown (its inline
      // `overflow: hidden` survives — Bootstrap restores it from the saved
      // attribute). Put the class back so the page stays in the state one open
      // Bootstrap modal implies; the dialog's own hide removes it later.
      if (dialog.classList.contains("show")) document.body.classList.add("modal-open")
      if (this.#nested.has(event.target)) this.#nested.set(event.target, null) // its backdrop is gone
    }
    dialog.addEventListener("hidden.bs.modal", this.onHidden)
    // A modal shown at BODY level over the dialog — every confirm / alert /
    // acknowledgement the order content raises mounts through lib/gl_modal_shell
    // on document.body, outside this element — strips body.modal-open when it
    // hides exactly like a descendant does, but its hidden event never passes
    // through the dialog, so the listener above cannot see it. Watched at the
    // document instead: while this dialog is shown, any other modal's hide puts
    // the class back (its own inline overflow survives, Bootstrap restores it).
    // The shell returns focus nowhere, and its show() already deactivated this
    // dialog's Bootstrap focus trap (FocusTrap#activate offs every other trap),
    // so a keyboard user's next Tab would walk out into the page: focus is put
    // back on the dialog itself, where the shared gl-modal-focus-trap wraps it.
    this.onForeignHidden = (event) => {
      if (!(event.target instanceof Element) || event.target === dialog || dialog.contains(event.target)) return
      if (!dialog.classList.contains("show")) return
      document.body.classList.add("modal-open")
      if (!dialog.contains(document.activeElement)) dialog.focus()
    }
    document.addEventListener("hidden.bs.modal", this.onForeignHidden)
    // Bootstrap appends a shown modal's backdrop to <body> synchronously inside
    // show(), right after dispatching the bubbling `show.bs.modal`. Snapshot the
    // backdrops on that event and diff one microtask later — show() has
    // returned by then — to learn which backdrop the descendant owns.
    this.onNestedShow = (event) => {
      const nested = event.target
      if (nested === dialog) {
        this.#opening = true // Bootstrap's show() has started; hide() is a no-op until shown
        return
      }
      if (!(nested instanceof Element)) return
      const before = new Set(document.querySelectorAll(".modal-backdrop"))
      queueMicrotask(() => {
        if (this.#dialog !== dialog || !nested.isConnected) return
        const owned = [...document.querySelectorAll(".modal-backdrop")].find((element) => !before.has(element)) ?? null
        this.#nested.set(nested, owned)
      })
    }
    dialog.addEventListener("show.bs.modal", this.onNestedShow)
    // The open transition is over: a close parked meanwhile runs now, as a
    // real Bootstrap hide — hidden.bs.modal → afterHidden, like any other.
    this.onShown = (event) => {
      if (event.target !== dialog) return
      this.#opening = false
      if (!this.#hideWhenShown) return
      this.#hideWhenShown = false
      this.#instance?.hide()
    }
    dialog.addEventListener("shown.bs.modal", this.onShown)
    // Bootstrap's own dismissers — the X / Cancel (`data-bs-dismiss`, a
    // delegated document listener) and a click on the .modal element itself
    // (the backdrop area) — call its hide() directly, which drops the request
    // during the fade-in. This listener runs first (element before document)
    // and parks those; once shown, Bootstrap handles them as before.
    this.onDismissClick = (event) => {
      if (!this.#opening || !(event.target instanceof Element)) return
      const dismisser = event.target.closest('[data-bs-dismiss="modal"]')
      const backdropArea = event.target === dialog
      if (backdropArea || (dismisser && dismisser.closest(".modal") === dialog)) this.hide()
    }
    dialog.addEventListener("click", this.onDismissClick)
    // Bootstrap's Escape handler is a plain bubbling keydown listener on each
    // modal element (#14097 B2): Escape pressed inside a nested modal reaches
    // the nested Modal first (it hides) and then bubbles to this element's own
    // Bootstrap listener, which would hide the dialog too. This listener is
    // registered BEFORE Modal.getOrCreateInstance adds Bootstrap's, so on the
    // same element it runs first and stopImmediatePropagation() keeps the
    // dialog's Bootstrap handler out of a keypress that was not aimed at it.
    this.onKeydown = (event) => {
      if (event.key !== "Escape") return
      const origin = event.target instanceof Element ? event.target.closest(".modal") : null
      if (origin && origin !== dialog) return event.stopImmediatePropagation()
      // Aimed at this dialog while it is still fading in: Bootstrap's own
      // handler (next on this element) would call a no-op hide(). Park it.
      if (this.#opening) this.hide()
    }
    dialog.addEventListener("keydown", this.onKeydown)
    this.#instance = Modal.getOrCreateInstance(dialog, { focus: true })
    this.#backdrop = backdropAddedBy(() => this.#instance.show())
    document.dispatchEvent(new CustomEvent(SHOWN_EVENT, { detail: { url: url.href } }))
  }

  hide() {
    if (this.#instance && this.#dialog) {
      if (this.#opening) {
        this.#hideWhenShown = true // replayed on shown.bs.modal
        return
      }
      this.#instance.hide() // → hidden.bs.modal → afterHidden
      return
    }
    this.dispose()
  }

  afterHidden(url) {
    this.removeDialog()
    this.restoreFocus()
    document.dispatchEvent(new CustomEvent(HIDDEN_EVENT, { detail: { url: url?.href } }))
  }

  // Cancel everything now: a pending fetch, the overlay borrow, the shown
  // dialog (without animation), the Bootstrap instance and its backdrop.
  dispose({ keepTrigger = false } = {}) {
    this.#generation += 1
    this.#abort?.abort()
    this.#abort = null
    this.#release?.()
    this.#release = null
    this.removeDialog()
    this.#pending = false
    if (!keepTrigger) this.#trigger = null
  }

  removeDialog() {
    if (this.#dialog && this.onHidden) this.#dialog.removeEventListener("hidden.bs.modal", this.onHidden)
    if (this.#dialog && this.onKeydown) this.#dialog.removeEventListener("keydown", this.onKeydown)
    if (this.#dialog && this.onNestedShow) this.#dialog.removeEventListener("show.bs.modal", this.onNestedShow)
    if (this.#dialog && this.onShown) this.#dialog.removeEventListener("shown.bs.modal", this.onShown)
    if (this.#dialog && this.onDismissClick) this.#dialog.removeEventListener("click", this.onDismissClick)
    if (this.onForeignHidden) document.removeEventListener("hidden.bs.modal", this.onForeignHidden)
    this.onForeignHidden = null
    this.onHidden = null
    this.onKeydown = null
    this.onNestedShow = null
    this.onShown = null
    this.onDismissClick = null
    this.#opening = false
    this.#hideWhenShown = false
    // Descendants first: each nested modal that was ever shown is settled to its
    // own hidden state and disposed, so no instance, backdrop or listener of it
    // outlives the subtree. Only a backdrop a descendant's show() added is
    // touched — never another modal's on the page.
    for (const [nested, backdrop] of this.#nested) {
      const instance = Modal.getInstance(nested)
      if (!instance) continue
      settleModal(instance, nested, backdrop)
      instance.dispose()
    }
    this.#nested.clear()
    if (this.#instance) {
      settleModal(this.#instance, this.#dialog, this.#backdrop) // a real close, run to its end synchronously
      this.#instance.dispose()
    }
    this.#instance = null
    this.#backdrop = null
    this.#dialog?.remove()
    this.#dialog = null
  }

  // Focus goes back to the trigger — or, when the trigger is an item of a
  // menu that closed while the dialog was open (display: none, so `focus()`
  // is a silent no-op and focus would land on <body>), to that menu's
  // trigger: the control the keyboard user actually reaches next.
  restoreFocus() {
    const trigger = this.#trigger
    this.#trigger = null
    focusReturnTarget(trigger)?.focus()
  }

  // A redirect is never followed into the dialog AND never visited by its
  // target (#14097 R1/R2): fetch has already FOLLOWED it, so the response the
  // adapter holds is the destination page — which consumed the flash the gate
  // attached (purchasing disabled, aged store down) and, for a sign-in redirect,
  // stored this fetch's `?store_modal=1` URL as the post-login return. Visiting
  // response.url would show the destination without its message. Instead the
  // browser is sent to the PLAIN href: the server issues the same redirect to a
  // real navigation, the flash renders there, and any return URL it stores is
  // the page, not the fragment. Off-origin targets take the same road.
  navigateAfterRedirect(redirectedTo, requested) {
    this.fallback(requested, `redirected to ${redirectedTo}`)
  }

  // The deep-link page shows the true state (error, denial, sign-in) — never a
  // half-rendered or stale dialog.
  fallback(url, reason) {
    console.warn(`[store-order-modal] falling back to ${url.pathname}: ${reason}`)
    browserNavigation.assign(url.href)
  }
}
